// src/contexts/MediaStreamContext.tsx
// Version 2.12 - Passage à 12 slots (images, sons, jingles, PiP)
// v2.30 — SourceAudioConfig étendu : volumeLeft/Right, audioDeviceId, audioEffects
// v2.31 — VideoSource.type étendu : 'scene' | 'jingle' (pipelines indépendants)
// v2.32 — VideoSource.xtream : credentials Xtream optionnels pour sources 'url'

import { WWW_URLS } from '../backendServices/urls';
import React, { createContext, useContext, useState, useRef, useCallback, ReactNode, useEffect } from 'react';
import { RemoteConnection } from '../dataTypes/liveStreamManagment/types_webrtc';
import { buildNegotiatedCodec, NegotiatedCodecInfo } from '../dataTypes/liveStreamManagment/utils_codec';
import { XtreamCredentials } from '../dataTypes/liveStreamManagment/sourceRegistry/types';
import { restoreMediaUrl, hasMediaRecord } from '../backendServices/liveStreamManagment/local_media_store';
import { isStreamedMediaUrl } from '../dataTypes/liveStreamManagment/utils_videoSource';
import { loadDeckEntries, loadStoredKeyMap, loadDeckSettings, restoreDeckFromServer, deviceRemapFromServer } from '../dataTypes/liveStreamManagment/deck_persistence';
import {
  scheduleAutosave, markRestored, fetchAutosave, downloadAutosaveMedia, markUploaded,
  AutosaveSnapshot,
} from '../backendServices/liveStreamManagment/regie_autosave';

export type { RemoteConnection };

export type AudioOnlyVisual = 'black' | 'spectrum' | 'oscilloscope';

export interface VideoSource {
  id: string;
  label: string;
  /** 'audio' : emplacement « audio seul », sans image (cf. utils_audioOnlySource.ts). */
  type: 'device' | 'remote' | 'screen' | 'url' | 'scene' | 'jingle' | 'final' | 'audio';
  /** Type 'audio' : l'entrée audio fixée au choix de la source (absente pour « Vide »). */
  deviceId?: string;
  /** Type 'audio' : image générée — écran noir (défaut), analyseur de spectre ou oscilloscope du son. */
  audioVisual?: AudioOnlyVisual;
  externalUrl?: string;
  stream?: MediaStream;
  /** Type 'screen' uniquement : true si une capture est effectivement en cours pour cet emplacement. */
  screenActive?: boolean;
  /**
   * Type 'remote' uniquement : l'appareil est tombé, mais on l'attend encore.
   *
   * La source survit à la déconnexion pour que son emplacement de deck lui reste
   * réservé — sans quoi la vignette disparaît, le remplissage automatique reloge
   * la caméra ailleurs à son retour, et l'opérateur doit la remettre à la main.
   * Elle n'a pas de `stream` : rien à afficher, seulement une place tenue.
   */
  awaiting?: boolean;
  screenConfig?: {
    width: number;
    height: number;
    frameRate: number;
    cursor: 'always' | 'motion' | 'never';
    audio: boolean;
  };
  /**
   * Credentials Xtream Codes associés à cette source (type 'url' uniquement).
   * Présent uniquement si la source a été ajoutée via le panneau Xtream.
   * Permet au reste du code (probe, watchdog, affichage) de distinguer
   * une source Xtream d'une URL HLS nue.
   */
  xtream?: XtreamCredentials;
}

// ── v2.7 : régie maître/esclave ───────────────────────────────────────────────

export type StreamDestination = 'mediamtx' | 'master';

export interface MasterConfig {
  wsUrl    : string;
  deviceId : string;
  password : string;
  label    : string;
}

export const DEFAULT_MASTER_CONFIG: MasterConfig = {
  wsUrl: '', deviceId: '', password: '', label: '',
};

export interface StreamSettings {
  videoBitrate      : number;
  audioBitrate      : number;
  videoQuality      : string;
  serverUrl         : string;
  // `streamPath` et `signalingUrl` ont été RETIRÉS le 16/08/2026. Ils portaient en
  // dur l'UUID d'une chaîne de production, et servaient de repli partout dans la
  // régie : sans UUID résolu, celle-ci travaillait sur cette chaîne-là sous
  // l'identité de celle qu'on avait ouverte — jusqu'à y publier en WHIP.
  //
  // Le chemin d'une chaîne ne se règle pas : il EST la chaîne ouverte. Il vient
  // désormais de `broadcastingChannel.uuid`, ou de rien du tout.
  streamDestination : StreamDestination;
  masterConfig      : MasterConfig;
  canvasWidth       : number;
  canvasHeight      : number;
  managerUrl        : string;
  managerApiKey     : string;
  // Transport des sources devices : 'p2p' = PeerConnection directe device↔régie
  // (historique), 'sfu' = le device publie en WHIP sur MediaMTX et la régie
  // tire le flux en WHEP (path src_{uuid}_{deviceId}). Repli : 'p2p'.
  deviceTransport   : 'p2p' | 'sfu';
  // Par où le RETOUR d'antenne rejoint les téléphones. Question distincte du
  // transport montant, et c'est voulu : les deux sens n'ont pas les mêmes
  // contraintes.
  //
  // 'mediamtx' = la régie publie son canvas UNE fois et le serveur le duplique.
  // C'est ce qui passe à l'échelle : en direct, la régie ouvre une
  // PeerConnection par téléphone, donc autant d'encodages et autant de fois le
  // même débit montant — quatre appareils à 2 Mb/s, ce sont 8 Mb/s et quatre
  // encodeurs contre 2 Mb/s et un seul.
  //
  // 'p2p' = la régie pousse son canvas à chacun. Plus cher, mais le trajet est le
  // plus court, et surtout il ne dépend de RIEN : le retour existe dès que la
  // régie est ouverte.
  //
  // ⚠️ La voie serveur lit `webrtc_{uuid}`, qui n'est alimenté que par la
  // publication WHIP du programme — donc uniquement pendant la diffusion. Hors
  // antenne elle ne donne rien, et le device bascule alors de lui-même en direct.
  returnTransport   : 'p2p' | 'mediamtx';
}

export const DEFAULT_SETTINGS: StreamSettings = {
  videoBitrate      : 2500,
  audioBitrate      : 128,
  videoQuality      : 'high',
  serverUrl         : WWW_URLS.broadcastingServer,
  streamDestination : 'mediamtx',
  masterConfig      : DEFAULT_MASTER_CONFIG,
  canvasWidth       : 1920,
  canvasHeight      : 1080,
  managerUrl        : WWW_URLS.broadcastingServer,
  managerApiKey     : '_Broadcast06',
  deviceTransport   : 'sfu',
  returnTransport   : 'mediamtx',
};

// ── v2.10 : configuration partage d'écran ─────────────────────────────────────

export interface ScreenShareConfig {
  frameRate : 15 | 30 | 60;
  audio     : boolean;
  width     : number;
  height    : number;
  cursor    : 'always' | 'motion' | 'never';
}

export const DEFAULT_SCREEN_SHARE_CONFIG: ScreenShareConfig = {
  frameRate : 30,
  audio     : false,
  width     : 1920,
  height    : 1080,
  cursor    : 'always',
};

// v3.0 : 4 emplacements de partage d'écran simultanés (au lieu d'un singleton) —
// même pattern que les slots image/son/PiP/jingle ci-dessous (SLOT_COUNT).
export const SLOT_COUNT_SCREEN = 4;

export interface ScreenShareSlot {
  id     : string;
  label  : string;
  config : ScreenShareConfig;
}

export const DEFAULT_SCREEN_SHARE_SLOTS: ScreenShareSlot[] = Array.from({ length: SLOT_COUNT_SCREEN }, (_, i) => ({
  id: `screen-${i}`, label: `Copie d'écran ${i + 1}`, config: { ...DEFAULT_SCREEN_SHARE_CONFIG },
}));

// ── v2.4 : codec négocié WHIP ─────────────────────────────────────────────────

export type NegotiatedWhipCodec = NegotiatedCodecInfo;
export { buildNegotiatedCodec };

const WHIP_CODEC_STORAGE_KEY = 'mediartc_negotiated_whip_codec';

function loadStoredCodec(): NegotiatedWhipCodec | null {
  try { return JSON.parse(localStorage.getItem(WHIP_CODEC_STORAGE_KEY) || 'null'); }
  catch { return null; }
}

// ── Nombre de slots par catégorie ─────────────────────────────────────────────

export const SLOT_COUNT     = 12;
export const SLOT_COUNT_PIP = 12;

// ── v2.5 : Mediaboard — images overlay + sons ─────────────────────────────────

export interface ImageSlot {
  id      : string;
  label   : string;
  url     : string;
  instantMediaId?: string;  
  /** Nom du fichier tel que choisi par l'opérateur (avec extension), conservé dans les sauvegardes. */
  originalName?   : string;
  hasMedia?      : boolean;
  x       : number;
  y       : number;
  width   : number;
  height  : number;
  opacity : number;
}

export interface SoundSlot {
  id     : string;
  label  : string;
  url    : string;
  instantMediaId?: string;
  /** Nom du fichier tel que choisi par l'opérateur (avec extension), conservé dans les sauvegardes. */
  originalName?   : string;
  hasMedia?      : boolean;
  loop   : boolean;
  volume : number;
  trimStart?: number; // secondes — début de la portion jouée (défaut : 0)
  trimEnd?  : number; // secondes — fin de la portion jouée (défaut : durée totale)
}

export const DEFAULT_IMAGE_SLOTS: ImageSlot[] = Array.from({ length: SLOT_COUNT }, (_, i) => ({
  id: `img-${i}`, label: `Image ${i + 1}`, url: '',
  x: 0, y: 0, width: 25, height: 25, opacity: 1,
}));

export const DEFAULT_SOUND_SLOTS: SoundSlot[] = Array.from({ length: SLOT_COUNT }, (_, i) => ({
  id: `snd-${i}`, label: `Son ${i + 1}`, url: '', loop: false, volume: 1,
}));

// ── v2.6 : PiP — incrustation vidéo dans la vidéo ────────────────────────────

export type PipSourceType = 'device' | 'screen' | 'remote';

export interface VideoOverlaySlot {
  id             : string;
  label          : string;
  sourceType     : PipSourceType;
  deviceId?      : string;
  remoteDeviceId?: string;
  x              : number;
  y              : number;
  width          : number;
  height         : number;
  opacity        : number;
  borderRadius   : number;
  borderWidth    : number;
  borderColor    : string;
}

export const DEFAULT_VIDEO_OVERLAY_SLOTS: VideoOverlaySlot[] = Array.from({ length: SLOT_COUNT_PIP }, (_, i) => ({
  id: `pip-${i}`, label: `PiP ${i + 1}`,
  sourceType: 'device' as PipSourceType, deviceId: '', remoteDeviceId: '',
  x: 2, y: 70, width: 22, height: 22,
  opacity: 1, borderRadius: 8, borderWidth: 2, borderColor: '#ffffff',
}));

// ── v2.8 : Jingles vidéo ──────────────────────────────────────────────────────

export interface JingleSlot {
  id        : string;
  label     : string;
  url       : string;
  instantMediaId?: string;
  /** Nom du fichier tel que choisi par l'opérateur (avec extension), conservé dans les sauvegardes. */
  originalName?   : string;
  hasMedia?      : boolean;
  useCanvas : boolean;
  volume    : number;
  /** Incrustation, en % du cadre (comme ImageSlot). Absents = plein cadre. */
  x?        : number;
  y?        : number;
  width?    : number;
  height?   : number;
  opacity?  : number;
}

export const DEFAULT_JINGLE_SLOTS: JingleSlot[] = Array.from({ length: SLOT_COUNT }, (_, i) => ({
  id: `video-${i}`, label: `Vidéo ${i + 1}`, url: '', useCanvas: false, volume: 1,
}));

// ── v2.11 : Layouts — dispositions prédéfinies ───────────────────────────────

export type LayoutBackgroundType = 'none' | 'image' | 'video' | 'source';

// Mode d'adaptation de l'image/vidéo de fond par rapport au cadre de la scène :
// - native    : taille réelle du média, centré, sans mise à l'échelle
// - stretchXY : étiré/contracté sur X et Y pour remplir exactement le cadre (déforme l'image)
// - stretchX  : étiré/contracté uniquement sur X pour correspondre à la largeur (hauteur native)
// - stretchY  : étiré/contracté uniquement sur Y pour correspondre à la hauteur (largeur native)
// - repeatX   : dupliqué (mosaïque) horizontalement à taille native
// - repeatY   : dupliqué (mosaïque) verticalement à taille native
// - repeatXY  : dupliqué (mosaïque) sur X et Y à taille native
// Si non défini : comportement historique — ajusté en conservant les proportions (letterboxing).
export type LayoutBackgroundFit =
  | 'native' | 'stretchXY' | 'stretchX' | 'stretchY' | 'repeatX' | 'repeatY' | 'repeatXY';

export interface LayoutBackground {
  type     : LayoutBackgroundType;
  url      : string;
  sourceId : string;
  fit?     : LayoutBackgroundFit;
  instantMediaId?: string;
  /** Nom du fichier tel que choisi par l'opérateur (avec extension), conservé dans les sauvegardes. */
  originalName?   : string;
  hasMedia?      : boolean;
}

export interface LayoutIncrustation {
  id       : string;
  type     : 'image' | 'video' | 'source';
  url      : string;
  sourceId : string;
  label    : string;
  instantMediaId?: string; 
  /** Nom du fichier tel que choisi par l'opérateur (avec extension), conservé dans les sauvegardes. */
  originalName?   : string;
  hasMedia?      : boolean;
  x        : number;
  y        : number;
  width    : number;
  height   : number;
  opacity  : number;
  zIndex   : number;
}

export interface LayoutSlot {
  id            : string;
  label         : string;
  background    : LayoutBackground;
  incrustations : LayoutIncrustation[];
}

const DEFAULT_LAYOUT_BACKGROUND: LayoutBackground = {
  type: 'none', url: '', sourceId: '',
};

export const DEFAULT_LAYOUT_SLOTS: LayoutSlot[] = Array.from({ length: SLOT_COUNT }, (_, i) => ({
  id            : `scene-${i}`,
  label         : `Scène ${i + 1}`,
  background    : { ...DEFAULT_LAYOUT_BACKGROUND },
  incrustations : [],
}));

// ── v2.28 / v2.30 : Audio par source — volume + mute + stéréo + effets ──────

// v2.31 — Record ouvert (pas une interface à champs fixes) : un nouvel effet audio
// ajouté au registre (cf. audioEffects/registry.ts) doit pouvoir y écrire son booléen
// et sa config sans qu'aucun type ici n'ait besoin d'être retouché.
export type SourceAudioEffects = Record<string, boolean>;

export interface SourceAudioConfig {
  sourceId      : string;
  volume        : number;      // 0–1 (volume global, conservé pour compat)
  muted         : boolean;
  // v2.30
  // Gain LINÉAIRE, pas un pourcentage : 1 = 0 dB. Le fader de SourceConfigModal court de
  // -60 dB à +6 dB, la valeur monte donc jusqu'à ~1.995 (AudioSourceRegistry borne à 2).
  volumeLeft?   : number;      // gain canal gauche
  volumeRight?  : number;      // gain canal droit
  audioDeviceId?: string;      // deviceId du micro associé à cette source
  audioEffects? : SourceAudioEffects;
  /** Réglages fins par effet (fréquence du lowcut, mix de la reverb...), keyés par id
   * d'effet — même principe que VideoEffect.defaultConfig côté vidéo. */
  audioEffectConfigs?: Record<string, unknown>;
}

const SOURCE_AUDIO_CONFIG_KEY = 'mediartc_source_audio_configs';

function loadSourceAudioConfigs(): SourceAudioConfig[] {
  try {
    const raw = localStorage.getItem(SOURCE_AUDIO_CONFIG_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SourceAudioConfig[];
  } catch { return []; }
}

function saveSourceAudioConfigs(configs: SourceAudioConfig[]): void {
  try { localStorage.setItem(SOURCE_AUDIO_CONFIG_KEY, JSON.stringify(configs)); } catch {}
}

// Effets audio (booléens + configs par effet) — cloisonnés PAR CHAÎNE, contrairement
// à volume/mute/deviceId ci-dessus (qui restent globaux : le micro et le niveau
// choisis pour une source ont vocation à rester les mêmes partout). Constaté
// explicitement : un effet réglé sur une source dans une chaîne revenait actif tout
// seul en rouvrant la MÊME source physique (webcam, slot d'écran — cf. leurs id
// stables, pas propres à une chaîne) dans une AUTRE chaîne — useAudioMetering.
// applyStoredLevels réapplique automatiquement l'état stocké à chaque connexion,
// sans action de l'opérateur dans la nouvelle chaîne.
export interface SourceAudioEffectsRecord {
  sourceId: string;
  audioEffects?: SourceAudioEffects;
  audioEffectConfigs?: Record<string, unknown>;
}

function sourceAudioEffectsKey(channelUuid: string): string {
  return `mediartc_source_audio_effects:${channelUuid}`;
}

function loadSourceAudioEffects(channelUuid: string): SourceAudioEffectsRecord[] {
  try {
    const raw = localStorage.getItem(sourceAudioEffectsKey(channelUuid));
    if (!raw) return [];
    return JSON.parse(raw) as SourceAudioEffectsRecord[];
  } catch { return []; }
}

function saveSourceAudioEffects(channelUuid: string, records: SourceAudioEffectsRecord[]): void {
  try { localStorage.setItem(sourceAudioEffectsKey(channelUuid), JSON.stringify(records)); } catch {}
}

// ── Persistance localStorage ──────────────────────────────────────────────────

const STORAGE_KEYS = {
  imageSlots        : 'mediartc_image_slots',
  soundSlots        : 'mediartc_sound_slots',
  videoOverlaySlots : 'mediartc_pip_slots',
  jingleSlots       : 'mediartc_jingle_slots',
  layoutSlots       : 'mediartc_layout_slots',
  screenShareSlots  : 'mediartc_screen_share_slots',
  settings          : 'mediartc_settings',
} as const;

function loadSlots<T>(key: string, defaults: T[]): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as T[];
    return defaults.map((def, i) => parsed[i] ? { ...def, ...parsed[i] } : def);
  } catch { return defaults; }
}

function saveSlots<T>(key: string, slots: T[]): void {
  try { localStorage.setItem(key, JSON.stringify(slots)); } catch {}
}

// ── Réhydratation des blob: URL ───────────────────────────────────────────────
//
// slot.url est une blob: URL, morte dès que la page est rechargée ; localStorage
// la conserve pourtant telle quelle. Résultat : le cadre du média reste (url non
// vide) mais l'image est cassée. Le fichier, lui, est toujours dans IndexedDB
// sous slot.instantMediaId : on en régénère une URL vivante après chaque
// chargement des slots. Un média absent d'IndexedDB (autre poste, cache vidé)
// garde son url — on ne touche à rien sans certitude.
async function freshUrls(ids: Array<string | undefined>): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  await Promise.all(Array.from(new Set(ids.filter((i): i is string => !!i))).map(async id => {
    const u = await restoreMediaUrl(id);
    if (u) out.set(id, u);
  }));
  return out;
}

function withFreshUrl<T extends { url: string; instantMediaId?: string }>(o: T, fresh: Map<string, string>): T {
  const u = o.instantMediaId ? fresh.get(o.instantMediaId) : undefined;
  return u ? { ...o, url: u } : o;
}

function rehydrateSimpleSlots<T extends { url: string; instantMediaId?: string }>(
  loaded: T[],
  apply: (fn: (prev: T[]) => T[]) => void,
): void {
  if (!loaded.some(s => s.instantMediaId)) return;
  freshUrls(loaded.map(s => s.instantMediaId)).then(fresh => {
    if (fresh.size === 0) return;
    apply(prev => prev.map(s => withFreshUrl(s, fresh)));
  });
}

function rehydrateLayoutSlots(
  loaded: LayoutSlot[],
  apply: (fn: (prev: LayoutSlot[]) => LayoutSlot[]) => void,
): void {
  const ids: Array<string | undefined> = [];
  loaded.forEach(l => { ids.push(l.background?.instantMediaId); (l.incrustations ?? []).forEach(i => ids.push(i.instantMediaId)); });
  if (!ids.some(Boolean)) return;
  freshUrls(ids).then(fresh => {
    if (fresh.size === 0) return;
    apply(prev => prev.map(l => ({
      ...l,
      background   : withFreshUrl(l.background, fresh),
      incrustations: l.incrustations.map(i => withFreshUrl(i, fresh)),
    })));
  });
}

// ── Mediaboard (images/sons/PiP/jingles) — cloisonné par chaîne ──────────────
//
// Même principe que sourceAudioEffectsKey plus haut : sans chaîne ouverte, pas
// de clé, pas de persistance (on reste sur les valeurs par défaut).
function channelSlotsKey(baseKey: string, channelUuid: string): string {
  return `${baseKey}:${channelUuid}`;
}

function loadChannelSlots<T>(baseKey: string, channelUuid: string | null, defaults: T[]): T[] {
  if (!channelUuid) return defaults;
  const scopedKey = channelSlotsKey(baseKey, channelUuid);
  if (localStorage.getItem(scopedKey) != null) return loadSlots<T>(scopedKey, defaults);
  // Migration : avant le cloisonnement, tout le mediaboard vivait sous `baseKey`,
  // partagé entre toutes les chaînes. On le reprend une fois comme point de
  // départ pour la première chaîne ouverte après la mise à jour — sans quoi son
  // contenu semblerait avoir disparu du jour au lendemain.
  return loadSlots<T>(baseKey, defaults);
}

function saveChannelSlots<T>(baseKey: string, channelUuid: string | null, slots: T[]): void {
  if (!channelUuid) return;
  saveSlots(channelSlotsKey(baseKey, channelUuid), slots);
  // Sauvegarde automatique sur le serveur (différée, voir regie_autosave.ts).
  scheduleAutosave(channelUuid, () => readChannelSnapshot(channelUuid));
}

export function readChannelSnapshot(channelUuid: string): AutosaveSnapshot {
  return {
    deck  : loadDeckEntries(channelUuid),
    keymap: loadStoredKeyMap(channelUuid),
    deckSettings: loadDeckSettings(channelUuid),
    image: loadChannelSlots<ImageSlot>(STORAGE_KEYS.imageSlots, channelUuid, DEFAULT_IMAGE_SLOTS),
    son  : loadChannelSlots<SoundSlot>(STORAGE_KEYS.soundSlots, channelUuid, DEFAULT_SOUND_SLOTS),
    video: loadChannelSlots<JingleSlot>(STORAGE_KEYS.jingleSlots, channelUuid, DEFAULT_JINGLE_SLOTS),
    pip  : loadChannelSlots<VideoOverlaySlot>(STORAGE_KEYS.videoOverlaySlots, channelUuid, DEFAULT_VIDEO_OVERLAY_SLOTS),
  };
}

function loadSettings(): StreamSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.settings);
    if (!raw) return DEFAULT_SETTINGS;
    const stored = JSON.parse(raw) as Partial<StreamSettings>;
    return {
      ...DEFAULT_SETTINGS,
      ...stored,
      masterConfig : { ...DEFAULT_MASTER_CONFIG, ...(stored.masterConfig ?? {}) },
      canvasWidth  : stored.canvasWidth  ?? DEFAULT_SETTINGS.canvasWidth,
      canvasHeight : stored.canvasHeight ?? DEFAULT_SETTINGS.canvasHeight,
    };
  } catch { return DEFAULT_SETTINGS; }
}

function saveSettings(s: StreamSettings): void {
  try { localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(s)); } catch {}
}

// ── Interface du contexte ─────────────────────────────────────────────────────

interface MediaStreamContextType {
  localStream        : MediaStream | null;
  remoteStream       : MediaStream | null;
  currentStream      : MediaStream | null;
  isStreaming        : boolean;
  isRemoteConnected  : boolean;
  videoRef           : React.RefObject<HTMLVideoElement>;
  audioContextRef    : React.MutableRefObject<AudioContext | null>;
  peerConnectionRef  : React.MutableRefObject<RTCPeerConnection | null>;
  remotePcRef        : React.MutableRefObject<RTCPeerConnection | null>;
  wsRef              : React.MutableRefObject<WebSocket | null>;

  selectedVideoSource : string;
  selectedAudio       : string;
  videoSources        : VideoSource[];
  settings            : StreamSettings;

  remoteConnections    : Map<string, RemoteConnection>;
  setRemoteConnections : React.Dispatch<React.SetStateAction<Map<string, RemoteConnection>>>;

  setLocalStream         : React.Dispatch<React.SetStateAction<MediaStream | null>>;
  setRemoteStream        : React.Dispatch<React.SetStateAction<MediaStream | null>>;
  setCurrentStream       : React.Dispatch<React.SetStateAction<MediaStream | null>>;
  setIsStreaming         : React.Dispatch<React.SetStateAction<boolean>>;
  setIsRemoteConnected   : React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedVideoSource : React.Dispatch<React.SetStateAction<string>>;
  setSelectedAudio       : React.Dispatch<React.SetStateAction<string>>;
  setVideoSources        : React.Dispatch<React.SetStateAction<VideoSource[]>>;
  setSettings            : React.Dispatch<React.SetStateAction<StreamSettings>>;

  cleanupOnAppClose : () => void;

  negotiatedWhipCodec      : NegotiatedWhipCodec | null;
  setNegotiatedWhipCodec   : (codec: NegotiatedWhipCodec | null) => void;
  clearNegotiatedWhipCodec : () => void;

  imageSlots         : ImageSlot[];
  setImageSlots      : React.Dispatch<React.SetStateAction<ImageSlot[]>>;
  soundSlots         : SoundSlot[];
  setSoundSlots      : React.Dispatch<React.SetStateAction<SoundSlot[]>>;
  activeImageIds     : Set<string>;
  setActiveImageIds  : React.Dispatch<React.SetStateAction<Set<string>>>;
  activeSoundIds     : Set<string>;
  setActiveSoundIds  : React.Dispatch<React.SetStateAction<Set<string>>>;

  videoOverlaySlots      : VideoOverlaySlot[];
  setVideoOverlaySlots   : React.Dispatch<React.SetStateAction<VideoOverlaySlot[]>>;
  activeVideoOverlayIds  : Set<string>;
  setActiveVideoOverlayIds: React.Dispatch<React.SetStateAction<Set<string>>>;

  jingleSlots      : JingleSlot[];
  setJingleSlots   : React.Dispatch<React.SetStateAction<JingleSlot[]>>;
  activeJingleId   : string | null;
  setActiveJingleId: React.Dispatch<React.SetStateAction<string | null>>;

  screenShareSlots      : ScreenShareSlot[];
  setScreenShareSlots   : React.Dispatch<React.SetStateAction<ScreenShareSlot[]>>;
  screenShareStreams    : Map<string, MediaStream>;
  setScreenShareStreams : React.Dispatch<React.SetStateAction<Map<string, MediaStream>>>;

  // v2.11 — Layouts
  layoutSlots       : LayoutSlot[];
  setLayoutSlots    : React.Dispatch<React.SetStateAction<LayoutSlot[]>>;
  activeLayoutId    : string | null;
  setActiveLayoutId : React.Dispatch<React.SetStateAction<string | null>>;

  // v2.28 — Audio par source
  sourceAudioConfigs      : SourceAudioConfig[];
  setSourceAudioConfigs   : (configs: SourceAudioConfig[]) => void;
  getSourceAudioConfig    : (sourceId: string) => SourceAudioConfig;
  // Effets audio de la chaîne ouverte : SEULE source de vérité, lue par le pupitre
  // pour l'affichage ET réappliquée au graphe audio (useAudioMetering).
  channelAudioEffects        : SourceAudioEffectsRecord[];
  replaceChannelAudioEffects : (records: SourceAudioEffectsRecord[]) => void;

  // ── Sessions par chaîne ─────────────────────────────────────────────────────
  // La chaîne ouverte, et de quoi basculer de l'une à l'autre en conservant l'état
  // d'interface de chacune. Voir ChannelSession.
  activeChannelUuid       : string | null;
  // `adresse` est la référence PUBLIQUE de la chaîne : le contexte s'en sert
  // pour composer les URL qui sortent (signalisation, départ des devices).
  // `uuid` reste la clé de cloisonnement des sessions.
  openChannel             : (uuid: string | null, adresse?: string | null) => void;
  forgetChannel           : (uuid: string) => void;
  // À n'appeler qu'au DÉPART du créateur : c'est le seul moment où les caméras
  // sont coupées. Changer de chaîne ou d'onglet les laisse vivre.
  closeAllChannelSessions : () => void;
  // La régie prête sa WebSocket de contrôle, seul moyen de prévenir les appareils
  // au moment où la page s'en va — voir announceDepartureBeforeUnload.
  registerControlSocket   : (ref: React.MutableRefObject<WebSocket | null> | null) => void;
  updateSourceAudioConfig : (sourceId: string, update: Partial<Omit<SourceAudioConfig, 'sourceId'>>) => void;
}

const MediaStreamContext = createContext<MediaStreamContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * L'état d'une chaîne mis de côté pendant qu'on en regarde une autre.
 *
 * Le contexte est monté UNE fois, à la racine de l'application, et survivait donc à
 * tous les changements de chaîne : ce qu'on avait sélectionné, les sources
 * construites à partir de l'UUID, et surtout les connexions vivantes aux
 * smartphones passaient d'une chaîne à la suivante. Une caméra branchée sur une
 * chaîne apparaissait ainsi dans la régie d'une autre.
 *
 * Les connexions aux smartphones (`remoteConnections`) sont rangées ici AVEC le
 * reste, et elles restent VIVANTES. Un créateur qui passe d'une de ses chaînes à
 * une autre, ou qui va voir un autre onglet, ne doit pas voir ses caméras se
 * couper puis se renégocier : c'est long et c'est visible à l'antenne. Elles sont
 * simplement cloisonnées — chaque chaîne ne voit que les siennes, ce qui suffit à
 * supprimer la fuite d'affichage d'une chaîne à l'autre.
 *
 * Elles ne sont fermées qu'au départ du créateur : déconnexion ou fermeture de la
 * page, via closeAllChannelSessions().
 */
interface ChannelSession {
  selectedVideoSource   : string;
  selectedAudio         : string;
  videoSources          : VideoSource[];
  activeImageIds        : Set<string>;
  activeSoundIds        : Set<string>;
  activeVideoOverlayIds : Set<string>;
  activeJingleId        : string | null;
  activeLayoutId        : string | null;
  remoteConnections     : Map<string, RemoteConnection>;
  isRemoteConnected     : boolean;
  /**
   * Captures d'écran en cours, indexées par id de slot ('screen-0'…) — PAS de clé
   * de chaîne dans cet id, donc sans ce cloisonnement une capture démarrée sur une
   * chaîne restait visible sur toutes les autres (même Map globale, même id de
   * slot). Suit le même principe que remoteConnections juste au-dessus : la
   * capture continue en arrière-plan sur la chaîne quittée, pas fermée ici.
   */
  screenShareStreams    : Map<string, MediaStream>;
}

/**
 * Une session vierge — FABRIQUÉE à chaque fois, jamais partagée.
 *
 * C'était une constante de module. Or `remoteConnections` est une Map que les
 * couches WebRTC MUTENT en place (elles y ajoutent l'appareil qui se connecte,
 * via le ref qui pointe sur la même instance). La « session vide » accumulait
 * donc les connexions : dès qu'un appareil s'était connecté une fois, TOUTE
 * chaîne jamais visitée repartait avec lui.
 *
 * C'est ce que la trace a montré, sans ambiguïté possible :
 *
 *     openChannel restaure uuid=null sources=∅ connexions=er
 *
 * Restaurer « rien » ramenait la caméra. De là, la source `remote-er` était
 * reconstruite sur une chaîne où elle n'a rien à faire, et sa connexion — qui ne
 * peut pas s'y stabiliser — faisait varier `stream` à chaque passage, donc
 * réécrire `videoSources` à chaque rendu : la boucle de rendus qui figeait
 * l'application.
 *
 * Le même piège vaut pour les Set d'identifiants actifs ci-dessous.
 */
const emptyChannelSession = (): ChannelSession => ({
  selectedVideoSource   : '',
  selectedAudio         : '',
  videoSources          : [],
  activeImageIds        : new Set(),
  activeSoundIds        : new Set(),
  activeVideoOverlayIds : new Set(),
  activeJingleId        : null,
  activeLayoutId        : null,
  remoteConnections     : new Map(),
  isRemoteConnected     : false,
  screenShareStreams    : new Map(),
});

export const MediaStreamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [localStream,        setLocalStream]        = useState<MediaStream | null>(null);
  const [remoteStream,       setRemoteStream]        = useState<MediaStream | null>(null);
  const [currentStream,      setCurrentStream]       = useState<MediaStream | null>(null);
  const [isStreaming,        setIsStreaming]          = useState(false);
  const [isRemoteConnected,  setIsRemoteConnected]   = useState(false);

  const [selectedVideoSource, setSelectedVideoSource] = useState<string>('');
  const [selectedAudio,       setSelectedAudio]       = useState<string>('');
  const [videoSources,        setVideoSources]        = useState<VideoSource[]>([]);
  const [remoteConnections,   setRemoteConnections]   = useState<Map<string, RemoteConnection>>(new Map());

  const [settings, _setSettings] = useState<StreamSettings>(() => loadSettings());
  const setSettings: React.Dispatch<React.SetStateAction<StreamSettings>> = (action) => {
    _setSettings(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveSettings(next);
      return next;
    });
  };

  const [negotiatedWhipCodec, _setNegotiatedWhipCodec] = useState<NegotiatedWhipCodec | null>(
    () => loadStoredCodec()
  );
  const setNegotiatedWhipCodec = (codec: NegotiatedWhipCodec | null) => {
    _setNegotiatedWhipCodec(codec);
    try {
      if (codec) localStorage.setItem(WHIP_CODEC_STORAGE_KEY, JSON.stringify(codec));
      else        localStorage.removeItem(WHIP_CODEC_STORAGE_KEY);
    } catch {}
  };
  const clearNegotiatedWhipCodec = () => setNegotiatedWhipCodec(null);

  // La chaîne ouverte — déclarée ICI (avant son premier usage ci-dessous) plutôt
  // qu'avec le reste du bloc "Sessions par chaîne" plus bas, qui la déclarait à
  // l'origine : le mediaboard (images/sons/PiP/jingles) et les EFFETS audio
  // (cf. channelAudioEffects, plus bas) en ont besoin pour se cloisonner par
  // chaîne. setActiveChannelUuid reste piloté par openChannel/forgetChannel
  // plus bas — aucun autre changement de comportement, un simple déplacement.
  const [activeChannelUuid, setActiveChannelUuid] = useState<string | null>(null);

  // v2.33 — Mediaboard cloisonné par chaîne : images/sons/PiP/jingles/partages
  // d'écran posés sur une chaîne ne doivent plus apparaître sur une autre, ni
  // disparaître quand on revient sur la chaîne d'origine. Rechargés à chaque
  // changement de chaîne, jamais fusionnés entre chaînes — même principe que
  // channelAudioEffects. Voir aussi resetRegieContext (contexts/utils.tsx),
  // qui ne doit PLUS remettre ces slots à zéro : ils sont désormais une config
  // persistante par chaîne, pas un état de session à effacer au montage/arrêt.
  const [imageSlots, _setImageSlots] = useState<ImageSlot[]>(DEFAULT_IMAGE_SLOTS);
  useEffect(() => {
    const loaded = loadChannelSlots<ImageSlot>(STORAGE_KEYS.imageSlots, activeChannelUuid, DEFAULT_IMAGE_SLOTS);
    _setImageSlots(loaded);
    rehydrateSimpleSlots(loaded, _setImageSlots);
  }, [activeChannelUuid]);
  const setImageSlots: React.Dispatch<React.SetStateAction<ImageSlot[]>> = (action) => {
    _setImageSlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.imageSlots, activeChannelUuid, next);
      return next;
    });
  };

  const [soundSlots, _setSoundSlots] = useState<SoundSlot[]>(DEFAULT_SOUND_SLOTS);
  useEffect(() => {
    const loaded = loadChannelSlots<SoundSlot>(STORAGE_KEYS.soundSlots, activeChannelUuid, DEFAULT_SOUND_SLOTS);
    _setSoundSlots(loaded);
    rehydrateSimpleSlots(loaded, _setSoundSlots);
  }, [activeChannelUuid]);
  const setSoundSlots: React.Dispatch<React.SetStateAction<SoundSlot[]>> = (action) => {
    _setSoundSlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.soundSlots, activeChannelUuid, next);
      return next;
    });
  };

  const [activeImageIds,    setActiveImageIds]    = useState<Set<string>>(new Set());
  const [activeSoundIds,    setActiveSoundIds]    = useState<Set<string>>(new Set());

  const [videoOverlaySlots, _setVideoOverlaySlots] = useState<VideoOverlaySlot[]>(DEFAULT_VIDEO_OVERLAY_SLOTS);
  useEffect(() => {
    const loaded = loadChannelSlots<VideoOverlaySlot>(STORAGE_KEYS.videoOverlaySlots, activeChannelUuid, DEFAULT_VIDEO_OVERLAY_SLOTS);
    _setVideoOverlaySlots(loaded);
  }, [activeChannelUuid]);
  const setVideoOverlaySlots: React.Dispatch<React.SetStateAction<VideoOverlaySlot[]>> = (action) => {
    _setVideoOverlaySlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.videoOverlaySlots, activeChannelUuid, next);
      return next;
    });
  };
  const [activeVideoOverlayIds, setActiveVideoOverlayIds] = useState<Set<string>>(new Set());

  const [jingleSlots, _setJingleSlots] = useState<JingleSlot[]>(DEFAULT_JINGLE_SLOTS);
  useEffect(() => {
    const loaded = loadChannelSlots<JingleSlot>(STORAGE_KEYS.jingleSlots, activeChannelUuid, DEFAULT_JINGLE_SLOTS);
    _setJingleSlots(loaded);
    rehydrateSimpleSlots(loaded, _setJingleSlots);
  }, [activeChannelUuid]);
  const setJingleSlots: React.Dispatch<React.SetStateAction<JingleSlot[]>> = (action) => {
    _setJingleSlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.jingleSlots, activeChannelUuid, next);
      return next;
    });
  };
  const [activeJingleId, setActiveJingleId] = useState<string | null>(null);

  const [screenShareSlots, _setScreenShareSlots] = useState<ScreenShareSlot[]>(DEFAULT_SCREEN_SHARE_SLOTS);
  useEffect(() => {
    _setScreenShareSlots(loadChannelSlots<ScreenShareSlot>(STORAGE_KEYS.screenShareSlots, activeChannelUuid, DEFAULT_SCREEN_SHARE_SLOTS));
  }, [activeChannelUuid]);
  const setScreenShareSlots: React.Dispatch<React.SetStateAction<ScreenShareSlot[]>> = (action) => {
    _setScreenShareSlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.screenShareSlots, activeChannelUuid, next);
      return next;
    });
  };
  const [screenShareStreams, setScreenShareStreams] = useState<Map<string, MediaStream>>(new Map());

  // Restauration du mediaboard depuis la sauvegarde automatique du serveur : un
  // poste neuf reprend réglages ET fichiers ; un poste qui a déjà ses réglages ne
  // récupère que les fichiers dont IndexedDB n'a plus trace. Tant qu'elle n'est
  // pas terminée, rien n'est sauvegardé (markRestored) — on n'écrase jamais le
  // serveur avec des défauts.
  useEffect(() => {
    if (!activeChannelUuid) return;
    const uuid = activeChannelUuid;
    let cancelled = false;

    const restoreFamily = async <T extends { id: string; url: string; instantMediaId?: string }>(
      baseKey   : string,
      defaults  : T[],
      serverRaw : any[] | undefined,
      mediaType : 'image' | 'sound' | 'video' | null,
      configId  : number,
      apply     : (fn: (prev: T[]) => T[]) => void,
      fix?      : (sv: any) => any,   // retouche d'un emplacement du serveur avant reprise (PiP : webcam)
    ) => {
      if (!serverRaw) return;
      const server = fix ? serverRaw.map(fix) : serverRaw;
      const hasLocalKey = localStorage.getItem(channelSlotsKey(baseKey, uuid)) != null;
      const local       = loadChannelSlots<T>(baseKey, uuid, defaults);
      const media       = new Map<string, { url: string; instantMediaId: string }>();

      if (mediaType) {
        await Promise.all(server.filter(sv => sv.hasMedia).map(async sv => {
          const mine = local.find(l => l.id === sv.id);
          // Simple vérification : restoreMediaUrl créerait une adresse blob: pour rien.
          if (mine?.instantMediaId && await hasMediaRecord(mine.instantMediaId)) return;  // déjà là
          const got = await downloadAutosaveMedia(configId, mediaType, sv.id, sv.originalName);
          if (got) { media.set(sv.id, got); markUploaded(uuid, mediaType, sv.id, got.instantMediaId); }
        }));
      }
      if (cancelled) return;
      if (hasLocalKey && media.size === 0) return;

      apply(prev => prev.map(s => {
        const sv = server.find(x => x.id === s.id);
        let n: T = s;
        // Une url distante (vidéo HLS du compte) est reprise telle quelle ; une url
        // locale ne vaut rien ici, le fichier arrive par `media`.
        if (!hasLocalKey && sv) { const { hasMedia, ...cfg } = sv; n = { ...s, ...cfg, url: isStreamedMediaUrl(cfg.url) ? cfg.url : '', instantMediaId: undefined }; }
        const m = media.get(s.id);
        return m ? { ...n, ...m } : n;
      }));
    };

    (async () => {
      try {
        const saved = await fetchAutosave(uuid);
        if (saved && !cancelled) {
          const d = saved.data ?? {};
          // Un PiP désigne sa webcam par deviceId, propre au poste qui a écrit la
          // sauvegarde : on la ramène à une webcam de CE poste (même règle de rang que
          // le deck) ; sans équivalent, l'emplacement n'a plus de webcam plutôt qu'un
          // identifiant mort. Sans accès aux caméras, rien n'est touché.
          const { remap, localIds } = await deviceRemapFromServer(d);
          const fixPipDevice = (sv: any) => {
            const id = sv?.deviceId;
            if (!id) return sv;
            const target = remap.get(id);
            if (target) return { ...sv, deviceId: target };
            return localIds.size > 0 && !localIds.has(id) ? { ...sv, deviceId: '' } : sv;
          };
          await restoreDeckFromServer(uuid, d);
          // Effets audio repris du serveur : on relit le magasin de la chaîne.
          setChannelAudioEffects(loadSourceAudioEffects(uuid));
          await Promise.all([
            restoreFamily<ImageSlot>(STORAGE_KEYS.imageSlots, DEFAULT_IMAGE_SLOTS, d.image?.slots, 'image', saved.id, setImageSlots),
            restoreFamily<SoundSlot>(STORAGE_KEYS.soundSlots, DEFAULT_SOUND_SLOTS, d.son?.slots, 'sound', saved.id, setSoundSlots),
            restoreFamily<JingleSlot>(STORAGE_KEYS.jingleSlots, DEFAULT_JINGLE_SLOTS, d.video?.slots, 'video', saved.id, setJingleSlots),
            restoreFamily<any>(STORAGE_KEYS.videoOverlaySlots, DEFAULT_VIDEO_OVERLAY_SLOTS, d.pip?.slots, null, saved.id, setVideoOverlaySlots as any, fixPipDevice),
          ]);
        }
        // Seulement si le serveur a répondu : sur échec réseau, on ne sauvegarde pas
        // (un poste sans réglages écraserait sinon la sauvegarde par des défauts).
        markRestored(uuid);
        scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
      } catch (e) {
        console.warn('[RegieAutosave] Restauration impossible, sauvegarde automatique suspendue :', e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannelUuid]);

  // v2.11 — Layouts
  // Scènes cloisonnées PAR CHAÎNE, comme le reste du mediaboard : rechargées à chaque
  // changement de chaîne, jamais fusionnées. Voir aussi resetRegieContext
  // (contexts/utils.tsx), qui ne doit PLUS les remettre à zéro.
  const [layoutSlots, _setLayoutSlots] = useState<LayoutSlot[]>(DEFAULT_LAYOUT_SLOTS);
  useEffect(() => {
    const loaded = loadChannelSlots<LayoutSlot>(STORAGE_KEYS.layoutSlots, activeChannelUuid, DEFAULT_LAYOUT_SLOTS);
    _setLayoutSlots(loaded);
    rehydrateLayoutSlots(loaded, _setLayoutSlots);
  }, [activeChannelUuid]);
  const setLayoutSlots: React.Dispatch<React.SetStateAction<LayoutSlot[]>> = (action) => {
    _setLayoutSlots(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveChannelSlots(STORAGE_KEYS.layoutSlots, activeChannelUuid, next);
      return next;
    });
  };
  const [activeLayoutId, setActiveLayoutId] = useState<string | null>(null);

  // v2.28 — Audio par source (volume/mute/deviceId — reste GLOBAL, cf.
  // sourceAudioEffectsKey plus haut pour pourquoi les EFFETS, eux, sont séparés).
  const [sourceAudioConfigs, _setSourceAudioConfigs] = useState<SourceAudioConfig[]>(
    () => loadSourceAudioConfigs()
  );
  const setSourceAudioConfigs = useCallback((configs: SourceAudioConfig[]) => {
    _setSourceAudioConfigs(configs);
    saveSourceAudioConfigs(configs);
  }, []);

  // Effets audio de la chaîne OUVERTE — rechargés à chaque changement de chaîne (cf.
  // activeChannelUuid ci-dessus), jamais fusionnés entre chaînes.
  const [channelAudioEffects, setChannelAudioEffects] = useState<SourceAudioEffectsRecord[]>([]);
  useEffect(() => {
    setChannelAudioEffects(activeChannelUuid ? loadSourceAudioEffects(activeChannelUuid) : []);
  }, [activeChannelUuid]);

  const getSourceAudioConfig = useCallback((sourceId: string): SourceAudioConfig => {
    const found = sourceAudioConfigs.find(c => c.sourceId === sourceId);
    const base: SourceAudioConfig = found ?? { sourceId, volume: 1, muted: false };
    const effects = channelAudioEffects.find(r => r.sourceId === sourceId);
    // Écrase explicitement audioEffects/audioEffectConfigs même si `base` (le store
    // GLOBAL, qui pouvait encore porter d'anciennes valeurs d'avant ce cloisonnement)
    // en a — la source de vérité pour les effets est désormais TOUJOURS
    // channelAudioEffects, jamais le store global.
    return { ...base, audioEffects: effects?.audioEffects, audioEffectConfigs: effects?.audioEffectConfigs };
  }, [sourceAudioConfigs, channelAudioEffects]);

  // Remplacement en bloc (chargement d'une configuration, réinitialisation) : une
  // source absente de `records` repart sans effet.
  const replaceChannelAudioEffects = useCallback((records: SourceAudioEffectsRecord[]) => {
    setChannelAudioEffects(records);
    if (activeChannelUuid) {
      saveSourceAudioEffects(activeChannelUuid, records);
      const uuid = activeChannelUuid;
      scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
    }
  }, [activeChannelUuid]);

  const updateSourceAudioConfig = useCallback((sourceId: string, update: Partial<Omit<SourceAudioConfig, 'sourceId'>>) => {
    const { audioEffects, audioEffectConfigs, ...rest } = update;

    if (audioEffects !== undefined || audioEffectConfigs !== undefined) {
      setChannelAudioEffects(prev => {
        const idx      = prev.findIndex(r => r.sourceId === sourceId);
        const existing = idx >= 0 ? prev[idx] : { sourceId };
        const updated: SourceAudioEffectsRecord = {
          ...existing,
          ...(audioEffects !== undefined ? { audioEffects } : {}),
          ...(audioEffectConfigs !== undefined ? { audioEffectConfigs } : {}),
        };
        const next = idx >= 0 ? prev.map((r, i) => i === idx ? updated : r) : [...prev, updated];
        if (activeChannelUuid) {
          saveSourceAudioEffects(activeChannelUuid, next);
          const uuid = activeChannelUuid;
          scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
        }
        return next;
      });
    }

    if (Object.keys(rest).length > 0) {
      _setSourceAudioConfigs(prev => {
        const idx      = prev.findIndex(c => c.sourceId === sourceId);
        const existing = idx >= 0 ? prev[idx] : { sourceId, volume: 1, muted: false };
        const updated  = { ...existing, ...rest };
        const next     = idx >= 0
          ? prev.map((c, i) => i === idx ? updated : c)
          : [...prev, updated];
        saveSourceAudioConfigs(next);
        return next;
      });
    }
  }, [activeChannelUuid]);

  // ── Sessions par chaîne ─────────────────────────────────────────────────────

  const channelSessionsRef = useRef<Map<string, ChannelSession>>(new Map());

  // Lus depuis des rappels stables (départ du créateur, fermeture de page), où une
  // valeur capturée au rendu serait périmée.
  // Piloté par openChannel, pas resynchronisé depuis le rendu : c'est lui qui porte
  // l'idempotence, et le réécrire ici le ferait retomber sur une valeur périmée.
  const activeChannelUuidRef = useRef<string | null>(activeChannelUuid);
  // L'ADRESSE publique de chaque chaîne connue, indexée par son uuid.
  //
  // ⚠️ Une simple valeur ne suffit pas : le départ de la régie prévient les
  // devices de TOUTES les sessions mémorisées, pas seulement de la chaîne
  // ouverte. Il faut donc retrouver l'adresse de chacune.
  const channelAddressesRef = useRef<Map<string, string>>(new Map());

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  /**
   * La WebSocket de contrôle de la chaîne ouverte, prêtée par la régie.
   *
   * Elle appartient à LiveStreamManagerBootstrap ; le contexte a besoin d'y accéder
   * pour un cas précis : la fermeture de la page. Là, on ne peut rien ouvrir — mais
   * on peut encore émettre sur une socket déjà ouverte, et c'est ce qui permet de
   * prévenir proprement les smartphones avant de partir.
   */
  const controlSocketRef = useRef<React.MutableRefObject<WebSocket | null> | null>(null);

  const registerControlSocket = useCallback((ref: React.MutableRefObject<WebSocket | null> | null) => {
    controlSocketRef.current = ref;
  }, []);

  /**
   * Prévient les smartphones d'une chaîne que la régie les lâche.
   *
   * `force_disconnect` est le signal propre : le device stoppe sa publication,
   * libère sa caméra, ferme sa WS et ne retente pas de se reconnecter. Sans lui, il
   * verrait juste sa PeerConnection tomber, garderait la caméra allumée et
   * essaierait de revenir — vers une régie qui n'est plus là.
   *
   * Il transite par le canal de contrôle de LA chaîne concernée. Les chaînes en
   * arrière-plan n'en ont plus d'ouvert (il est fermé au changement de chaîne, cf.
   * useLiveStreamSetup) : on en ouvre donc un le temps de l'annonce.
   *
   * Best effort et sans attente : le départ du créateur ne doit jamais être retardé
   * ni empêché par un microservice qui ne répond pas.
   */
  const announceDepartureToDevices = useCallback((channelUuid: string, deviceIds: string[]) => {
    // ⚠️ Ces deux URL sortent vers le proxy : elles portent l'ADRESSE, pas
    // l'uuid — qui ne route plus depuis la fermeture de la voie live.
    const adresse = channelAddressesRef.current.get(channelUuid) ?? channelUuid;
    if (deviceIds.length === 0) { return; }

    try {
      const wsBase = settingsRef.current.serverUrl.replace(/^http/i, 'ws').replace(/\/$/, '');
      const ws     = new WebSocket(`${wsBase}/${adresse}/webrtc/signaling/control?label=Depart`);

      const closeSocket = () => { try { ws.close(); } catch {} };

      ws.onopen = () => {
        deviceIds.forEach(id => {
          try { ws.send(JSON.stringify({ type: 'force_disconnect', target: id })); } catch {}
        });
        // Laisse le temps aux trames de partir avant de refermer.
        setTimeout(closeSocket, 300);
      };

      ws.onerror = closeSocket;

      // Filet : une socket qui ne s'ouvre jamais ne doit pas rester en suspens.
      setTimeout(closeSocket, 3000);
    } catch {
      // Le départ prime sur l'élégance de l'au revoir.
    }
  }, []);


  // Lues au moment de la bascule, sans la faire dépendre de chaque frappe.
  const liveSessionRef = useRef<ChannelSession>(emptyChannelSession());
  liveSessionRef.current = {
    selectedVideoSource, selectedAudio, videoSources,
    activeImageIds, activeSoundIds, activeVideoOverlayIds,
    activeJingleId, activeLayoutId,
    remoteConnections, isRemoteConnected,
    screenShareStreams,
  };

  /**
   * Ouvre une chaîne : range l'état de celle qu'on quitte, restaure celui de
   * celle qu'on ouvre — ou repart à vide si on n'y est jamais allé.
   *
   * Appelée par la régie au montage et à chaque changement de chaîne. Idempotente :
   * réappeler avec la même chaîne ne fait rien, ce qui la rend sûre dans un effet.
   */
  const openChannel = useCallback((uuid: string | null, adresse?: string | null) => {
    if (uuid) { channelAddressesRef.current.set(uuid, adresse ?? uuid); }
    // L'idempotence se lit sur le ref, PAS dans un updater de setState. Un updater
    // doit rester pur : y appeler d'autres setState est interdit, et StrictMode le
    // rejoue — la session aurait été rangée deux fois.
    const previous = activeChannelUuidRef.current;

    if (previous === uuid) { return; }

    if (previous) { channelSessionsRef.current.set(previous, liveSessionRef.current); }

    const restored = (uuid && channelSessionsRef.current.get(uuid)) || emptyChannelSession();

    // Écrit tout de suite : deux appels dans le même rendu ne doivent pas se
    // dédoubler en attendant que l'état soit committé.
    activeChannelUuidRef.current = uuid;

    setActiveChannelUuid      (uuid);
    setSelectedVideoSource    (restored.selectedVideoSource);
    setSelectedAudio          (restored.selectedAudio);
    setVideoSources           (restored.videoSources);
    setActiveImageIds         (restored.activeImageIds);
    setActiveSoundIds         (restored.activeSoundIds);
    setActiveVideoOverlayIds  (restored.activeVideoOverlayIds);
    setActiveJingleId         (restored.activeJingleId);
    setActiveLayoutId         (restored.activeLayoutId);

    // Les connexions ne sont PAS fermées : elles suivent leur chaîne et l'attendent.
    // On ne remet à l'écran que celles de la chaîne ouverte — le cloisonnement
    // suffit, la renégociation serait du gâchis. Elles ne partent qu'avec le
    // créateur, dans closeAllChannelSessions().
    setRemoteConnections(restored.remoteConnections);
    setIsRemoteConnected(restored.isRemoteConnected);
    setScreenShareStreams(restored.screenShareStreams);
  }, []);

  /**
   * Oublie l'état mis de côté pour une chaîne — après suppression, par exemple.
   * Ferme ses connexions au passage : plus personne ne viendra les reprendre.
   */
  const forgetChannel = useCallback((uuid: string) => {
    const session = channelSessionsRef.current.get(uuid);

    if (session) {
      announceDepartureToDevices(uuid, Array.from(session.remoteConnections.keys()));
      session.remoteConnections.forEach(conn => { try { conn.peerConnection.close(); } catch {} });
    }

    channelSessionsRef.current.delete(uuid);
  }, [announceDepartureToDevices]);

  /**
   * Le créateur s'en va : déconnexion, ou fermeture de la page.
   *
   * C'est le SEUL moment où l'on coupe les caméras. Changer de chaîne ou d'onglet
   * les laisse en place — voir ChannelSession.
   */
  const closeAllChannelSessions = useCallback(() => {
    channelSessionsRef.current.forEach((session, uuid) => {
      announceDepartureToDevices(uuid, Array.from(session.remoteConnections.keys()));
      session.remoteConnections.forEach(conn => { try { conn.peerConnection.close(); } catch {} });
    });
    channelSessionsRef.current.clear();

    // La chaîne ouverte n'est pas dans la Map : elle est encore « en vol ».
    const live = liveSessionRef.current;

    if (activeChannelUuidRef.current) {
      announceDepartureToDevices(activeChannelUuidRef.current, Array.from(live.remoteConnections.keys()));
    }

    live.remoteConnections.forEach(conn => { try { conn.peerConnection.close(); } catch {} });

    setRemoteConnections(new Map());
    setIsRemoteConnected(false);

    // Le ref porte l'idempotence d'openChannel : sans cette remise à zéro, rouvrir
    // la même chaîne après une déconnexion ne restaurerait rien.
    activeChannelUuidRef.current = null;
    setActiveChannelUuid(null);
  }, [announceDepartureToDevices]);

  const videoRef          = useRef<HTMLVideoElement>(null);
  const audioContextRef   = useRef<AudioContext | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const remotePcRef       = useRef<RTCPeerConnection | null>(null);
  const wsRef             = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (videoRef.current && currentStream) videoRef.current.srcObject = currentStream;
  }, [currentStream]);

  const cleanupOnAppClose = () => {
    if (currentStream)      currentStream.getTracks().forEach(t => t.stop());
    screenShareStreams.forEach(stream => stream.getTracks().forEach(t => t.stop()));
    if (audioContextRef.current)    audioContextRef.current.close();
    if (peerConnectionRef.current)  peerConnectionRef.current.close();
    if (remotePcRef.current)        remotePcRef.current.close();
    if (wsRef.current)              wsRef.current.close();
    remoteConnections.forEach(conn => conn.peerConnection.close());

    // Les autres chaînes du créateur gardent leurs caméras ouvertes tant qu'il est
    // là : la page qui part est justement le moment où il ne l'est plus.
    channelSessionsRef.current.forEach(session => {
      session.remoteConnections.forEach(conn => { try { conn.peerConnection.close(); } catch {} });
    });
  };

  /**
   * L'au revoir aux smartphones quand la page s'en va.
   *
   * Ouvrir une WebSocket ici serait vain : le navigateur ne la laisserait pas
   * s'établir. On émet donc sur celle qui est DÉJÀ ouverte — celle de la chaîne
   * affichée — de façon strictement synchrone, avant toute fermeture. Sans cela le
   * téléphone garde sa caméra allumée et rappelle une régie qui n'existe plus.
   *
   * Les chaînes en arrière-plan n'ont pas de socket ouverte : leurs appareils ne
   * sont prévenus qu'à la déconnexion par le bouton, qui, elle, peut en rouvrir une.
   */
  const announceDepartureBeforeUnload = () => {
    const ws = controlSocketRef.current?.current;

    if (ws?.readyState === WebSocket.OPEN) {
      liveSessionRef.current.remoteConnections.forEach((_conn, deviceId) => {
        try { ws.send(JSON.stringify({ type: 'force_disconnect', target: deviceId })); } catch {} 
      });
    }

    // Les chaînes en arrière-plan n'ont plus de socket ouverte, et on ne peut pas en
    // ouvrir pendant l'unload. sendBeacon est le seul canal qui survit à cet
    // instant : le navigateur prend la requête en charge et la poste même une fois
    // la page partie. Aucune réponse n'est lue, et c'est très bien — un départ ne
    // doit pas dépendre de la bonne santé du microservice.
    const base = settingsRef.current.serverUrl.replace(/\/$/, '');

    channelSessionsRef.current.forEach((session, uuid) => {
      const deviceIds = Array.from(session.remoteConnections.keys());

      if (deviceIds.length === 0) { return; }

      try {
        // text/plain : sendBeacon n'autorise pas d'en-tête, et un Blob en
        // application/json déclencherait un preflight CORS que l'unload ne laisse
        // pas aboutir. Le endpoint lit donc le corps à la main.
        navigator.sendBeacon(
          `${base}/${channelAddressesRef.current.get(uuid) ?? uuid}/webrtc/remoteDevices/departure`,
          new Blob([JSON.stringify({ device_ids: deviceIds })], { type: 'text/plain' }),
        );
      } catch {}
    });
  };

  useEffect(() => {
    // 'pagehide' (pas 'beforeunload') : ne se déclenche que si la page part réellement,
    // pas juste parce qu'un dialogue beforeunload a été affiché puis annulé ailleurs
    // (cf. avertissement de fermeture en mode Régie dans LiveStreamManager.tsx).
    // L'annonce D'ABORD, la fermeture ensuite : l'ordre est le message.
    const onPageHide = () => { announceDepartureBeforeUnload(); cleanupOnAppClose(); };

    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStream, screenShareStreams, remoteConnections]);

  return (
    <MediaStreamContext.Provider value={{
      localStream, remoteStream, currentStream, isStreaming, isRemoteConnected,
      videoRef, audioContextRef, peerConnectionRef, remotePcRef, wsRef,
      selectedVideoSource, selectedAudio, videoSources, settings,
      remoteConnections, setRemoteConnections,
      setLocalStream, setRemoteStream, setCurrentStream, setIsStreaming, setIsRemoteConnected,
      setSelectedVideoSource, setSelectedAudio, setVideoSources, setSettings,
      cleanupOnAppClose,
      negotiatedWhipCodec, setNegotiatedWhipCodec, clearNegotiatedWhipCodec,
      imageSlots, setImageSlots,
      soundSlots, setSoundSlots,
      activeImageIds,    setActiveImageIds,
      activeSoundIds,    setActiveSoundIds,
      videoOverlaySlots, setVideoOverlaySlots,
      activeVideoOverlayIds, setActiveVideoOverlayIds,
      jingleSlots, setJingleSlots,
      activeJingleId, setActiveJingleId,
      screenShareSlots, setScreenShareSlots,
      screenShareStreams, setScreenShareStreams,
      layoutSlots, setLayoutSlots,
      activeLayoutId, setActiveLayoutId,
      sourceAudioConfigs, setSourceAudioConfigs,
      getSourceAudioConfig, updateSourceAudioConfig,
      channelAudioEffects, replaceChannelAudioEffects,
      activeChannelUuid, openChannel, forgetChannel, closeAllChannelSessions,
      registerControlSocket,
    }}>
      {children}
    </MediaStreamContext.Provider>
  );
};

export const useMediaStream = () => {
  const context = useContext(MediaStreamContext);
  if (!context) throw new Error('useMediaStream must be used within MediaStreamProvider');
  return context;
};